/*
 * Global audit-trail button — drop this onto ANY form's toolbar and it
 * Just Works.  Hits the shared /api/v1/audit/trail endpoint, opens a
 * popup with the columns every form expects: Date / User / Op /
 * Property / Old → New / Reason.
 *
 *  Usage:
 *    <AuditTrailButton formName="Custom Reporting" sourceId={jobOid} />
 *    <AuditTrailButton formName="MOR Adhoc Reporting" />
 *    <AuditTrailButton formName="Form Config" />
 *
 *  Props:
 *    formName  — required.  Matches the FormName column XAF writes in
 *                dbo.AuditData ('Custom Reporting', 'MOR Adhoc
 *                Reporting', 'Form Config', etc.).
 *    sourceId  — optional.  When set, narrows the audit log to a single
 *                record (JobOid, saved-report Oid, config-key, etc.).
 *                When unset, shows every audit row for the formName.
 *    sinceDate — optional yyyy-MM-dd lookback.  Defaults to 90 days
 *                server-side when not provided.
 *    label     — optional button label override.  Default: "Audit".
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { fmtLocal } from '../utils/datetime';
import { isAuditEnabled, useAuditTrailConfig } from '../utils/auditConfig';
import { AppDataGrid, type AppColumn } from './AppDataGrid';
import { AuditTimeline } from '../features/audit/AuditTimeline';

/** One row in the popup grid — matches the server's AuditEntry record. */
interface AuditRow {
  oid:             string;
  createdDate?:    string | null;
  createdBy?:      string | null;
  operationType?:  string | null;
  formName?:       string | null;
  sourceId?:       string | null;
  /** AuditData.ID — the record's human-readable id (a sample id, an LLT, a name…). */
  recordId?:       string | null;
  propertyName?:   string | null;
  oldValue?:       string | null;
  newValue?:       string | null;
  comment?:        string | null;
  reasonForChange?: string | null;
}

export interface AuditTrailButtonProps {
  formName:  string;
  /** Aggregate several AuditData.FormName values into ONE log (a screen whose child
   *  forms are audited under their own names but against the same record).  Sent as
   *  the `formNames` CSV the endpoint already supports; `formName` still names the
   *  popup and drives the Settings → Audit Trail Configuration gate. */
  formNames?: readonly string[];
  sourceId?: string;
  sinceDate?: string;          // yyyy-MM-dd
  label?:    string;
  /** Visual variant (BO UI Standard v2, 2026-07-01):
   *  - 'button' (default) — the `.ca-btn` white toolbar button (legacy).
   *  - 'chip'   — the QUIET title-row chip (`.audit-chip`, boStandard.css):
   *    small violet pill next to the load-time chip.  Audit Trail is
   *    VIEW-ONLY, so it lives in the title row's read-only zone — never in
   *    the action toolbar. */
  variant?:  'button' | 'chip';
  /** Optional click handler override — useful for legacy pages that
   *  already have their own audit popup wired up and just want the
   *  standardized button styling/icon.  When omitted, this component
   *  manages the popup itself. */
  onClick?:  () => void;
}

export function AuditTrailButton(p: AuditTrailButtonProps) {
  const [open, setOpen] = useState(false);
  // Config-driven (CLAUDE.md standing rule 11): the audit log surfaces only when
  // Settings → Audit Trail Configuration has audit ENABLED for this form.
  // useAuditTrailConfig re-renders this button when the admin toggles it.
  // Default is ON, so the button keeps showing until explicitly turned off.
  useAuditTrailConfig();
  if (!isAuditEnabled(p.formName)) return null;

  return (
    <>
      <button className={p.variant === 'chip' ? 'audit-chip' : 'ca-btn'}
              onClick={() => (p.onClick ? p.onClick() : setOpen(true))}
              title={p.sourceId
                ? `Audit Trail for ${p.formName} (this record)`
                : `Audit Trail for ${p.formName}`}>
        {/* The EXACT BO UI Standard prototype glyph (🛡️, violet class) —
            every form gets the same icon so the audit button is instantly
            recognisable everywhere in the app. */}
        <span className="tb-i tb-i-violet">🛡️</span> {p.label ?? 'Audit'}
      </button>
      {open && (
        <AuditTrailPopup
          formName={p.formName}
          formNames={p.formNames}
          sourceId={p.sourceId}
          sinceDate={p.sinceDate}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Popup — same chrome the Custom Reporting Audit popup uses today.
// Built as a separate component so any form using <AuditTrailButton>
// gets identical column layout, search, date-range, etc.
// ─────────────────────────────────────────────────────────────────────
// Exported so the SDMS detail views (SdmsPackageDetail / SdmsSpreadsheet) can
// mount the same shared audit popup scoped to a batch. (Merged 2026-07-07.)
export function AuditTrailPopup(p: {
  formName: string;
  formNames?: readonly string[];
  sourceId?: string;
  sinceDate?: string;
  onClose: () => void;
}) {
  const [rows,   setRows]   = useState<AuditRow[]>([]);
  const [load,   setLoad]   = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [err,    setErr]    = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Grid vs roadmap Timeline — same two views the Data Center audit log offers.
  const [view,   setView]   = useState<'grid' | 'timeline'>('grid');

  // A family of FormNames is one CSV param; a single name keeps the original
  // `formName` param, so every existing caller is byte-for-byte unchanged.
  const namesCsv = p.formNames && p.formNames.length > 1 ? p.formNames.join(',') : '';

  useEffect(() => {
    let alive = true;
    setLoad(true); setErr(null);
    const t0 = performance.now();
    api.get('/api/v1/audit/trail', {
      params: {
        ...(namesCsv ? { formNames: namesCsv } : { formName: p.formName }),
        sourceId:  p.sourceId,
        sinceDate: p.sinceDate,
      },
    })
      .then(res => {
        if (!alive) return;
        setRows((res.data as { data?: AuditRow[] }).data ?? []);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? 'Failed to load audit trail.';
        setErr(msg);
      })
      .finally(() => { if (alive) { setLoadMs(performance.now() - t0); setLoad(false); } });
    return () => { alive = false; };
  }, [p.formName, namesCsv, p.sourceId, p.sinceDate]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r => [
      r.createdBy, r.operationType, r.formName, r.recordId, r.propertyName,
      r.oldValue, r.newValue, r.comment, r.reasonForChange,
    ].some(v => String(v ?? '').toLowerCase().includes(t)));
  }, [rows, search]);

  // Local timezone, 24-hour, no AM/PM (CLAUDE.md §14b.6) — fmtLocal force-UTCs
  // the wire string then formats en-GB; falls back to the raw value if unparseable.
  const fmtDate = (v?: string | null) => (v ? (fmtLocal(v) || String(v)) : '');

  // Shared-grid columns.  Only When (custom date format) and Operation (status
  // pill) need a renderer.  ID says WHICH record a row is about — without it a
  // form-wide log (many samples, one popup) is unreadable.  It is shown only
  // when the form's audit rows carry one, so a form that never wrote an ID does
  // not gain an empty column.
  const hasRecordId = rows.some(r => !!r.recordId);
  const gridCols: AppColumn<AuditRow>[] = [
    { field: 'createdDate',   header: 'When',      width: 150,
      render: r => fmtDate(r.createdDate), text: r => fmtDate(r.createdDate) },
    { field: 'createdBy',     header: 'User',      width: 120 },
    // A family log mixes several child forms — say which one each row came from.
    ...(namesCsv ? [{ field: 'formName', header: 'Form', width: 170 } as AppColumn<AuditRow>] : []),
    { field: 'operationType', header: 'Operation', width: 120,
      render: r => (r.operationType ? <span className={'adg-pill ' + opVariant(r.operationType)}>{r.operationType}</span> : null),
      text:   r => r.operationType ?? '' },
    ...(hasRecordId ? [{ field: 'recordId', header: 'ID', width: 200 } as AppColumn<AuditRow>] : []),
    { field: 'propertyName',  header: 'Property',  width: 150 },
    { field: 'oldValue',      header: 'Old Value' },
    { field: 'newValue',      header: 'New Value' },
    { field: 'reasonForChange', header: 'Reason',  width: 180 },
  ];

  return (
    <div onClick={p.onClose}
         style={{
           position: 'fixed', inset: 0, zIndex: 4500,
           background: 'rgba(0,0,0,.35)',
           display: 'flex', alignItems: 'center', justifyContent: 'center',
         }}>
      <div onClick={e => e.stopPropagation()}
           style={{
             width: 1100, maxWidth: '95vw', maxHeight: '90vh',
             background: 'var(--ai-card)', borderRadius: 4,
             boxShadow: '0 6px 24px rgba(0,0,0,.25)',
             display: 'flex', flexDirection: 'column', overflow: 'hidden',
           }}>
        <div style={{
          padding: '8px 14px', background: 'var(--brand-accent, var(--ai-orange))', color: '#fff',
          fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>
            Audit Trail — {p.formName}
          </span>
          <button onClick={p.onClose} style={{
            background: 'transparent', border: 'none', color: '#fff',
            fontSize: 18, cursor: 'pointer', lineHeight: 1,
          }} title="Close">×</button>
        </div>

        <div style={{
          padding: '6px 12px', background: 'var(--ai-header-bg)',
          borderBottom: '1px solid var(--ai-line)',
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <input type="text" value={search}
                 onChange={e => setSearch(e.target.value)}
                 placeholder="Search across columns…"
                 style={{
                   padding: '4px 8px', width: 280,
                   border: '1px solid var(--ai-line)', borderRadius: 3, fontSize: 12,
                 }} />
          <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
            {filtered.length} of {rows.length} row{rows.length === 1 ? '' : 's'}
          </span>
          {/* Grid / Timeline toggle — same two views as the Data Center audit log. */}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', border: '1px solid var(--ai-line)', borderRadius: 6, overflow: 'hidden' }}>
            <button type="button" onClick={() => setView('grid')}
              style={{ border: 'none', cursor: 'pointer', fontSize: 12, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5,
                background: view === 'grid' ? 'var(--brand-accent, var(--ai-orange))' : 'var(--ai-card)', color: view === 'grid' ? '#fff' : 'var(--ai-muted)' }}>
              <i className="ti ti-table" /> Grid
            </button>
            <button type="button" onClick={() => setView('timeline')}
              style={{ border: 'none', cursor: 'pointer', fontSize: 12, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5,
                background: view === 'timeline' ? 'var(--brand-accent, var(--ai-orange))' : 'var(--ai-card)', color: view === 'timeline' ? '#fff' : 'var(--ai-muted)' }}>
              <i className="ti ti-timeline" /> Timeline
            </button>
          </span>
        </div>

        <div style={{ flex: 1, overflow: 'auto', borderTop: '1px solid var(--ai-line)', display: 'flex', flexDirection: 'column' }}>
          {err && !load ? (
            <div style={{ padding: 12, color: 'var(--ai-red)', fontSize: 12 }}>{err}</div>
          ) : view === 'timeline' ? (
            /* Shared roadmap timeline — identical to the Data Center audit log. */
            <AuditTimeline rows={filtered} loading={load} />
          ) : (
            /* Shared data grid — styling, per-column filters, sort, footer /
               pagination and load-time are all standardised in <AppDataGrid>. */
            <AppDataGrid<AuditRow>
              rows={filtered}
              columns={gridCols}
              getRowId={(r, i) => `${r.oid}-${i}`}
              loading={load}
              loadMs={loadMs}
              emptyText="No audit entries."
            />
          )}
        </div>

        <div style={{
          padding: '8px 14px', borderTop: '1px solid var(--ai-line)',
          background: 'var(--ai-bg)', display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button className="ca-btn" onClick={p.onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/** AppDataGrid status-pill variant by operation type — green for adds/insert,
 *  amber for updates, red for deletes, blue for everything else. */
function opVariant(op?: string | null): 'ok' | 'warn' | 'alert' | 'info' {
  const k = (op ?? '').toLowerCase();
  if (k.includes('add') || k.includes('insert') || k.includes('creat')) return 'ok';
  if (k.includes('update') || k.includes('edit') || k.includes('chang')) return 'warn';
  if (k.includes('delete') || k.includes('remove')) return 'alert';
  return 'info';
}
